<script lang="ts">
    import PanelHeader from "../panels/_components/PanelHeader.svelte";
    import { ActivityService } from "../../services/ActivityService";
    import Activity from "./Activity.svelte";
    import { AppStore } from "$/stores/AppStore";
    import { TenantStore } from "$/stores/TenantStore";
    import { getEffectivePricingPlan } from "$/utils/Utils";
    import { url } from "@roxi/routify";
    import { clickAction } from "$/utils/clickAction";

    import ZeroActivitiesImg from "$/images/error/0-Activities.png";

    export let topic;
    export let open;

    let activities = [];
    let hasMoreActivities = true;
    let showUpgradeButton = false;
    let loading = true;

    const paginationCount = 20;
    const daysLimit = 30;
    const millisecondsInADay = 24 * 60 * 60 * 1000;
    const dateLimit = Date.now() - daysLimit * millisecondsInADay;

    async function loadActivities() {
        let newActivities = await ActivityService.getActivities(
            topic.id,
            paginationCount,
        );
        let filteredActivities = newActivities;

        if (effectivePricingPlan < 1) {
            filteredActivities = newActivities.filter(
                (activity) => activity.createdOn >= dateLimit,
            );
            if (
                filteredActivities.length !== newActivities.length &&
                hasMoreActivities
            ) {
                showUpgradeButton = true;
            }
        }

        activities = filteredActivities;
        loading = false;

        if (newActivities.length < paginationCount) hasMoreActivities = false;
    }

    async function loadMoreActivities() {
        loading = true;
        let oldestRead =
            activities.length == 0
                ? null
                : activities[activities.length - 1].createdOn;

        let newActivities = await ActivityService.getActivities(
            topic.id,
            paginationCount,
            oldestRead,
        );
        let filteredActivities = newActivities;

        if (effectivePricingPlan < 1) {
            filteredActivities = newActivities.filter(
                (activity) => activity.createdOn >= dateLimit,
            );
            if (
                filteredActivities.length !== newActivities.length &&
                hasMoreActivities
            ) {
                showUpgradeButton = true;
            }
        }

        activities = [...activities, ...filteredActivities];

        if (newActivities.length < paginationCount) hasMoreActivities = false;
        loading = false;
    }

    $: effectivePricingPlan = $TenantStore?.active
        ? getEffectivePricingPlan($TenantStore?.active)
        : null;
    $: {
        //lazy load and reload on panel re-open
        //this logic will potentially be changed if side panel rendering logic changes
        if (topic && topic.id && open) loadActivities();
    }
</script>

<div class="activities-panel">
    <PanelHeader title="Activities" on:close={AppStore.closePanels} />

    <div class="activities">
        {#each activities as activity (activity.id)}
            <Activity {activity} />
        {/each}

        {#if loading}
            <p class="info-message">Loading activities...</p>
        {:else if activities.length === 0}
            <p class="info-message">
                {#if showUpgradeButton}
                    There are no activities within the last 30 days
                {:else}
                    <p class="no-activities">
                        <img
                            src={ZeroActivitiesImg}
                            alt="There are no activities, yet"
                        />
                    </p>
                    <p />
                {/if}
            </p>
        {/if}

        {#if showUpgradeButton}
            <div class="button-wrapper">
                <a class="upgrade-button" href={$url("/payment")}>
                    Upgrade for unlimited history
                </a>
            </div>
        {:else if hasMoreActivities && !loading}
            <div class="button-wrapper">
                <button use:clickAction={loadMoreActivities}>
                    Load more
                </button>
            </div>
        {/if}
    </div>
</div>

<style>
    @import "../../main.css";

    .activities-panel {
        @apply reset flex w-full flex-col bg-gray-100 h-full !important;
    }

    .activities {
        @apply flex flex-1 flex-col overflow-y-auto;
    }

    .button-wrapper {
        @apply p-2;
    }

    button {
        @apply w-full select-none rounded bg-gray-100 py-1 text-sm hover:bg-gray-200;
    }

    .upgrade-button {
        @apply block w-full select-none rounded bg-yellow-300 py-1 text-center text-sm hover:bg-yellow-400;
    }

    .info-message {
        @apply p-4 pb-2 text-center text-sm;
    }

    .no-activities {
        @apply bg-gray-100 px-10 py-52 text-sm;
    }
</style>
